import React, { useEffect } from 'react'
import s from '@styles/common/VentiWidget.module.scss'

declare global {
    interface Window {
        VentiPayWidget?: (opts: {
            cartCurrency: string
            element: string
            language: string
            priceMin: number
            source: string
            removeAnchorLogo: boolean
        }) => void
    }
}

type VentiPayWidgetProps = {
    productId: string | number
    finalPrice: number
    language?: string
}

const injectVentiScript = (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        if (window.VentiPayWidget) {
            resolve()
            return
        }

        const existingScript = document.querySelector(
            'script[src*="ventipay.com/widgets"]'
        )
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve(), {
                once: true
            })
            return
        }

        const script = document.createElement('script')
        script.src = 'https://js.ventipay.com/widgets/ecommerce/product/'
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = () =>
            reject(new Error('Failed to load VentiPay script'))

        document.body.appendChild(script)
    })
}

export const VentiPayWidget = ({
    productId,
    finalPrice,
    language = 'es-CL'
}: VentiPayWidgetProps) => {
    const widgetId = `_ventipay_wdgt_${productId}`

    useEffect(() => {
        if (typeof window === 'undefined') return

        let cancelled = false
        let retryTimeout: ReturnType<typeof setTimeout> | undefined

        const renderWidget = (): boolean => {
            const el = document.getElementById(widgetId)
            if (!window.VentiPayWidget || !el || cancelled) {
                return false
            }

            el.innerHTML = ''
            window.VentiPayWidget({
                cartCurrency: 'CLP',
                element: `#${widgetId}`,
                language,
                priceMin: finalPrice > 0 ? finalPrice : 0,
                source: 'adobe-commerce',
                removeAnchorLogo: true
            })
            el.querySelector('svg')?.setAttribute('height', '16px')
            return true
        }

        const tryRender = (retries = 0) => {
            if (cancelled) return

            if (renderWidget()) {
                return
            }

            if (retries < 10) {
                retryTimeout = setTimeout(() => tryRender(retries + 1), 200)
            } else {
                console.warn(
                    'VentiPayWidget failed to initialize after retries'
                )
            }
        }

        const init = async () => {
            try {
                await injectVentiScript()
                tryRender()
            } catch (err) {
                console.error('Error loading VentiPay widget:', err)
            }
        }

        void init()

        return () => {
            cancelled = true
            if (retryTimeout) {
                clearTimeout(retryTimeout)
            }
        }
    }, [productId, finalPrice, language, widgetId])

    return <div id={widgetId} className={s['venti-widget']} />
}
